import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider } from 'react-hook-form';
import { MarketDivider } from '@market/react';
import { useCheckoutFlow } from 'typescript/context/checkout_flow';
import { useLocaleInfo } from 'typescript/context/locale_info';
import { useSavedCardsContext } from 'typescript/context/saved_cards';
import useFinanceForm from 'typescript/apps/checkout_v3/components/payment/pay_in_installments/finance_form/use_finance_form';
import Card from 'typescript/apps/checkout_v3/components/payment/card';
import {
  usePaymentForm,
  usePaymentSdk,
} from 'typescript/utilities/web_payment_sdk';
import { useGetOrderSummary } from 'typescript/hooks/use_order_actions_client';
import {
  useGetApplicationFlow,
  useSubmitApplicationFlow,
} from 'typescript/hooks/capital_shop/use_application_flows_client';
import SavedCardsOptions from 'typescript/apps/checkout_v3/components/payment/saved_cards/saved_cards_options';
import {
  orderApplicationFlowToken,
  orderIsWarranty,
  userIsLoggedIn,
} from 'typescript/utilities/order_data';
import PageLoader from 'typescript/components/page_loader';
import {
  useListOffers,
  useSelectOffer,
} from 'typescript/hooks/use_offers_client';
import { type Options } from 'typescript/components/option_rows/option_rows';
import { useIsProcessingAnyRequest } from 'typescript/hooks/use_react_query_utils';
import {
  buildOptionFromOffer,
  isAnyOfferSelected,
} from 'typescript/utilities/offers';
import {
  buildSubmitApplicationFlowRequestData,
  applicationReviewed,
} from 'typescript/utilities/application_flow';
import { useToaster } from 'typescript/hooks/use_toaster';
import { usePaymentContext } from 'typescript/context/payment_context';
import { useSubmitOrder } from 'typescript/apps/checkout_v3/components/payment/use_submit_order';
import useAddressForm from 'typescript/apps/checkout_v3/components/address_form/use_address_form';
import { AddressType } from 'typescript/schemas/address_forms/address_fields';
import { buildBillingAddress } from 'typescript/utilities/address_utils';
import SubmitButton from 'typescript/apps/checkout_v3/components/payment/submit_button';
import BillingAddress from 'typescript/apps/checkout_v3/components/payment/billing_address';
import EmptyStateWithLoading from 'typescript/components/empty_state_with_loading';
import Disclaimer from 'typescript/apps/checkout_v3/components/payment/disclaimer';
import { PaymentType } from 'typescript/constants/enums/payment';
import useHandleFinancingSubmission from 'typescript/apps/checkout_v3/components/payment/pay_in_installments/use_handle_financing_submission';
import DisclosureModal from 'typescript/apps/checkout_v3/components/payment/pay_in_installments/disclosure_modal';
import FinanceApp from 'typescript/apps/checkout_v3/components/payment/pay_in_installments/finance_app';
import {
  CHECKOUT_PAGE_CLICK_EVENTS,
  COMPONENT_NAMES,
} from 'typescript/constants/tracking';
import {
  onCheckoutPageClick,
  onCheckoutPageViewCombined,
} from 'typescript/utilities/tracking';
import OptionRowsWithLogging from 'typescript/apps/checkout_v3/components/option_rows_with_logging';
import { useCompleteOrderContext } from 'typescript/context/complete_order_context';
import styles from 'typescript/apps/checkout_v3/components/payment/pay_in_installments/pay_in_installments.module.scss';

// TODO(bdang): Investigate refactoring into smaller logical components
const PayInInstallments = ({ className = '' }) => {
  const { t } = useTranslation();
  const { showToast } = useToaster();
  const { countryCode } = useLocaleInfo();

  const {
    submittingPayment,
    setSubmittingPayment,
    sameBillingAsShipping,
    pollingCapitalShop,
    setPollingCapitalShop,
    disclosureDocumentToken,
    showDisclosureModal,
  } = usePaymentContext();

  const { setShowCompletingOrderDialog, setShowAlternateCompletingCopy } =
    useCompleteOrderContext();

  const { isProcessing } = useIsProcessingAnyRequest();

  const { formMethods: financeFormMethods } = useFinanceForm({
    countryCode,
    validationMode: 'onTouched',
  });
  const {
    formState: { isValid: isFinanceFormValid },
    getValues: getFinanceFormValues,
  } = financeFormMethods;

  const { formMethods: addressFormMethods } = useAddressForm({
    countryCode,
    addressType: AddressType.billing,
  });
  const {
    formState: { isValid: isAddressFormValid },
    getValues: getAddressFormValues,
  } = addressFormMethods;

  const { orderNumber, trackingData } = useCheckoutFlow();
  const { orderSummaryData, isPending: orderDataIsPending } =
    useGetOrderSummary({ orderNumber });
  const applicationFlowToken = orderApplicationFlowToken(
    orderSummaryData?.installment_summary,
  );

  const { applicationFlowData, isPending: applicationFlowDataIsPending } =
    useGetApplicationFlow({
      applicationFlowToken,
      enabled: Boolean(applicationFlowToken),
      ...(pollingCapitalShop && { refetchInterval: 1500 }),
    });
  const applicationFlowAttributes = applicationFlowData?.data?.attributes;
  const { submitApplicationFlow } = useSubmitApplicationFlow();

  const { submitOrder } = useSubmitOrder(orderNumber);

  // This prevents re-rendering of usePaymentForm unless the payment schedule changes,
  // avoiding re-rendering on irrelevant order summary data changes.
  const memoizedOrderSummaryData = useMemo(
    () => orderSummaryData,
    [orderSummaryData?.payment_due?.payment_schedule],
  );

  const scriptLoaded = usePaymentSdk();
  const { sqCard, sqPayments } = usePaymentForm(
    scriptLoaded,
    memoizedOrderSummaryData,
  );

  const {
    savedCardsEnabled,
    makeDefaultCard,
    setMakeDefaultCard,
    saveCardToWallet,
    setSaveCardToWallet,
  } = useSavedCardsContext();

  const { selectOffer } = useSelectOffer({ orderNumber });
  const { offersData, isPending: offersDataIsPending } = useListOffers({
    orderNumber,
  });
  const offers = offersData?.offers;
  const offerOptions: Options[] = useMemo(() => {
    if (offersDataIsPending) {
      return [];
    }
    return offers.map((offer) =>
      buildOptionFromOffer({
        offer,
        disabled: submittingPayment,
      }),
    );
  }, [offersData, offersDataIsPending, isProcessing, submittingPayment]);

  // Auto select an offer if no offer is selected yet. This will happen on first
  // load of the page.
  useEffect(() => {
    if (offers?.length > 0 && !isAnyOfferSelected(offers)) {
      selectOffer({ selectedOfferToken: offers[0].token });
    }
  }, [offersData]);

  const onOfferSelect = ({ optionId }) => {
    onCheckoutPageClick({
      eventDesc: CHECKOUT_PAGE_CLICK_EVENTS.offer_select,
      trackingData,
    });
    selectOffer({ selectedOfferToken: optionId });
  };

  const { handleDecisionStatusChange, handleFinancingDisclosure } =
    useHandleFinancingSubmission();

  useEffect(() => {
    handleDecisionStatusChange({
      decisionStatus: applicationFlowAttributes?.status,
    });
  }, [applicationFlowAttributes?.status]);

  // eslint-disable-next-line require-await
  const handleSubmit = async () => {
    setSubmittingPayment(true);
    setShowCompletingOrderDialog(true);

    onCheckoutPageClick({
      eventDesc: CHECKOUT_PAGE_CLICK_EVENTS.payment_page_cta_installments,
      trackingData,
    });

    // If already approved for installments, don't submit application flow again
    // and skip to next step.
    if (applicationReviewed(applicationFlowAttributes)) {
      handleFinancingDisclosure();
    } else {
      setShowAlternateCompletingCopy(true);
      const financeFormValues = getFinanceFormValues();
      submitApplicationFlow(
        {
          applicationFlowToken,
          applicationFlowData: buildSubmitApplicationFlowRequestData(
            financeFormValues.personal_ssn,
            financeFormValues.personal_birthdate,
            applicationFlowAttributes,
          ),
        },
        {
          onSuccess: () => {
            setPollingCapitalShop(true);
          },
          onError: () => {
            setSubmittingPayment(false);
            setShowCompletingOrderDialog(false);
            showToast({
              message:
                'Failed to submit Financing Application, please try again',
              variant: 'critical',
            });
          },
        },
      );
    }
  };

  const handleDisclosureSubmit = async () => {
    await submitOrder({
      paymentMethod: sqCard,
      paymentType: PaymentType.CARD,
      billingAddress: buildBillingAddress({
        sameBillingAsShipping,
        billingAddressFormValues: getAddressFormValues(),
        shippingAddress: orderSummaryData?.shipping_address,
        contactInformation: orderSummaryData?.contact_information,
      }),
      sqPayments: sqPayments.current,
      financingDisclosureAcknowledged: true,
    });
  };

  useEffect(() => {
    onCheckoutPageViewCombined({
      componentName: COMPONENT_NAMES.APPS.CHECKOUT_V3.PAY_IN_INSTALLMENTS,
      additionalParameters: trackingData,
    });
  }, []);

  if (orderDataIsPending) {
    return <PageLoader />;
  }

  return (
    <div className={`shop-grid-container ${className}`}>
      {(disclosureDocumentToken || showDisclosureModal) && (
        <DisclosureModal
          submitOrder={handleDisclosureSubmit}
          applicationFlowToken={applicationFlowToken}
        />
      )}
      {offersDataIsPending || applicationFlowDataIsPending ? (
        <EmptyStateWithLoading className="shop-grid-item-full" showLoading />
      ) : (
        <OptionRowsWithLogging
          className="shop-grid-item-full"
          header={t('checkout.payment_v3.payInInstallments.offers.title')}
          options={offerOptions}
          onOptionSelect={onOfferSelect}
          componentName={COMPONENT_NAMES.APPS.CHECKOUT_V3.INSTALLMENT_OFFERS}
        />
      )}
      <FormProvider {...financeFormMethods}>
        <form className="shop-grid-item-full">
          <FinanceApp applicationFlowAttributes={applicationFlowAttributes} />
        </form>
      </FormProvider>
      <Card
        className="shop-grid-item-full"
        sqCard={sqCard}
        canUseSavedCard={false}
      />
      <SavedCardsOptions
        className="shop-grid-item-full"
        userIsLoggedIn={userIsLoggedIn(orderSummaryData?.current_user)}
        orderIsWarranty={orderIsWarranty(orderSummaryData)}
        savedCardsEnabled={savedCardsEnabled}
        saveCardToWallet={saveCardToWallet}
        setSaveCardToWallet={setSaveCardToWallet}
        makeDefaultCard={makeDefaultCard}
        setMakeDefaultCard={setMakeDefaultCard}
      />
      <FormProvider {...addressFormMethods}>
        <form className="shop-grid-item-full">
          <BillingAddress className="shop-grid-item-full" />
        </form>
      </FormProvider>
      <div className={`${styles.divider} shop-grid-item-full`}>
        <MarketDivider size="thin" />
      </div>
      {/* The subscription disclosure/consent intentionally lives in
          DisclosureModal: "Apply now" only submits the credit application —
          the order (and its subscription) completes from the modal. */}
      <SubmitButton
        className="shop-grid-item-full"
        disabled={
          isProcessing ||
          (!applicationReviewed(applicationFlowAttributes) &&
            !isFinanceFormValid) ||
          (!sameBillingAsShipping && !isAddressFormValid) ||
          submittingPayment
        }
        buttonCTA={
          applicationReviewed(applicationFlowAttributes)
            ? t('checkout.payment_v3.disclosures.completeCTA')
            : t('checkout.payment_v3.payInInstallments.applyNow')
        }
        onClick={handleSubmit}
      />
      <Disclaimer
        className="shop-grid-item-full"
        applicationFlowToken={applicationFlowToken}
        showDivider={false}
      />
    </div>
  );
};

export default PayInInstallments;
